/**
 * IXVerify v2 — Dashboard, the module home (ixv2 WP-3.2 + WP-3.3 merged;
 * plan §4 screens inventory: "Dashboard (recent sessions, 'New Dispense',
 * pending checks)").
 *
 * "New Dispense" (the 1-tap live-capture entry → /ixverify/dispense), the
 * staged Ready-to-dispense queue (top cards — the 15-second at-cabinet flow),
 * the coordinator staging affordance (upload/paste any time before the visit,
 * §3.5 — staged create → chained kill-switched `/extract`, WP-3.1 overnight
 * decision 1), recent sessions with protocol labels + a "View all history"
 * hand-off to the WP-3.3 History page. Live-updates over the
 * `ixv_session_update` SSE event (falls back to manual refresh in local
 * Supabase mode).
 *
 * Clicking a recent session that already HAS a report opens the report
 * preview, not the wizard: a sealed session's resume screen is a dead end (a
 * verification code and two buttons), so the report is the only thing there is
 * to look at. Everything still in flight resumes the wizard as before.
 *
 * Data: the PHI-free `sessionSummary` projection only (no DOB anywhere near
 * this surface — R3); studies are loaded once to label sessions with their
 * protocol.
 *
 * Cross-device step A (2026-09-18): "In progress" — MY in-flight sessions
 * (draft / capturing / review), listed first so a dispense started on the
 * desktop is one tap away on the phone (and vice versa). It rides the same
 * `ixv_session_update` tick, so the card appears the moment the other device
 * creates the session. Those ids are dropped from Recent so a session never
 * shows twice.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, CalendarClock, Inbox, LayoutDashboard, Loader2, MonitorSmartphone, Plus, UploadCloud } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { PageHeader, Modal, useToast, EmptyState, RefreshButton, primaryBtnCls, secondaryBtnCls, fieldCls } from '../../ui';
import { subscribeIxvSessionUpdates } from '../../lib/realtimeTransport';
import * as ixv from '../lib/api';
import SessionCard from '../components/shared/SessionCard';
import ReportPreview from '../components/shared/ReportPreview';

/** Statuses that have a published report to read (matches History). */
const REPORTABLE = new Set(['completed', 'second_check_pending', 'verified']);
/** Statuses a dispense can be resumed from — the "In progress" section. */
const IN_FLIGHT = new Set(['draft', 'capturing', 'review']);

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, currentSite } = useAuth();
  const { toast } = useToast();

  const [staged, setStaged] = useState<ixv.IxvSessionSummary[] | null>(null);
  const [inProgress, setInProgress] = useState<ixv.IxvSessionSummary[] | null>(null);
  const [recent, setRecent] = useState<ixv.IxvSessionSummary[] | null>(null);
  const [studies, setStudies] = useState<ixv.IxvStudy[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const [reportTarget, setReportTarget] = useState<ixv.IxvSessionSummary | null>(null);

  const [stageOpen, setStageOpen] = useState(false);
  const [stageText, setStageText] = useState('');
  const [stageFile, setStageFile] = useState<File | null>(null);
  const [staging, setStaging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [stagedRes, mineRes, recentRes, studyList] = await Promise.all([
        ixv.listSessions({ status: 'staged', limit: 25 }),
        ixv.listSessions({ mine: true, limit: 25 }),
        ixv.listSessions({ limit: 15 }),
        ixv.listStudies().catch(() => [] as ixv.IxvStudy[]),
      ]);
      const mine = mineRes.sessions.filter((s) => IN_FLIGHT.has(s.status));
      const mineIds = new Set(mine.map((s) => s.id));
      setStaged(stagedRes.sessions);
      setInProgress(mine);
      setRecent(recentRes.sessions.filter((s) => s.status !== 'staged' && !mineIds.has(s.id)));
      setStudies(studyList);
      setLoadError(null);
    } catch (err) {
      setLoadError(ixv.errorMessage(err, 'Could not load sessions'));
      setStaged([]);
      setInProgress([]);
      setRecent([]);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, currentSite?.id]);

  // Live refresh on session updates (SSE `ixv_session_update`) — plus a
  // catch-up reload whenever the page comes back into view (landing review,
  // 2026-09-18): a tick only reaches an OPEN stream and missed ones are never
  // replayed, so a phone that wakes up on this page — the cross-device
  // pick-up point — must not show yesterday's lists. Going hidden is not a wake.
  useEffect(() => {
    if (!user?.id) return undefined;
    const unsubscribe = subscribeIxvSessionUpdates(user.id, () => void load());
    const wake = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      void load();
    };
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('pageshow', wake);
    return () => {
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('pageshow', wake);
      unsubscribe();
    };
  }, [user?.id, load]);

  const protocolById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of studies) map.set(s.id, s.protocol_id);
    return map;
  }, [studies]);

  const protocolFor = useCallback(
    (session: ixv.IxvSessionSummary) => (session.site_study_id && protocolById.get(session.site_study_id)) || null,
    [protocolById]
  );

  const openSession = useCallback(
    (session: ixv.IxvSessionSummary) => {
      if (REPORTABLE.has(session.status)) {
        setReportTarget(session);
        return;
      }
      navigate(`/ixverify/dispense/${session.id}`);
    },
    [navigate]
  );

  const submitStage = useCallback(async () => {
    if (!stageFile && !stageText.trim()) return;
    setStaging(true);
    try {
      const { session } = await ixv.createStagedSession(
        stageFile ? { file: stageFile, filename: stageFile.name } : { text: stageText.trim() }
      );
      // Chain the kill-switched extraction so the queue card goes green before
      // the visit (WP-3.1 overnight decision 1 — the client chains /extract).
      try {
        await ixv.extractSession(session.id);
        toast('Assignment staged — ready to dispense', { type: 'success' });
      } catch (err) {
        if (ixv.isKillSwitched(err)) {
          toast('Staged. Extraction is disabled right now — it will read at dispense time.', { type: 'success' });
        } else {
          toast(ixv.errorMessage(err, 'Staged, but the assignment could not be read yet'), { type: 'error' });
        }
      }
      setStageOpen(false);
      setStageFile(null);
      setStageText('');
      await load();
    } catch (err) {
      toast(ixv.errorMessage(err, 'Could not stage the assignment'), { type: 'error' });
    } finally {
      setStaging(false);
    }
  }, [stageFile, stageText, load, toast]);

  const loading = staged == null || inProgress == null || recent == null;

  return (
    <div>
      <PageHeader
        title="Dashboard"
        icon={LayoutDashboard}
        subtitle="Verify IP dispensing against the IXRS assignment"
        actions={
          <div className="flex items-center gap-2">
            <RefreshButton onClick={() => void load()} loading={refreshing} />
            <button type="button" className={secondaryBtnCls} onClick={() => setStageOpen(true)}>
              <UploadCloud className="h-4 w-4" aria-hidden="true" /> Stage assignment
            </button>
            <button
              type="button"
              className={`${primaryBtnCls} px-4 py-2`}
              onClick={() => navigate('/ixverify/dispense')}
              data-testid="new-dispense"
            >
              <Plus className="h-4 w-4" aria-hidden="true" /> New Dispense
            </button>
          </div>
        }
      />

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-primary-600 motion-reduce:animate-none dark:text-primary-400" aria-hidden="true" />
        </div>
      ) : loadError ? (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300" role="alert">
          {loadError}
        </div>
      ) : (
        <>
        {/* In progress — MY in-flight sessions, first: the cross-device
            hand-off is "start on the desktop, pick it up here" (step A). */}
        {inProgress.length > 0 && (
          <section aria-label="In progress" className="mt-4" data-testid="in-progress">
            <div className="mb-2 flex items-center gap-2">
              <MonitorSmartphone className="h-4 w-4 text-primary-600 dark:text-primary-400" aria-hidden="true" />
              <h2 className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                In progress <span className="tabular-nums">({inProgress.length.toLocaleString()})</span>
              </h2>
            </div>
            <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
              Your dispenses still open — including ones started on another device. Open one to keep going here.
            </p>
            <ul className="grid gap-2 xl:grid-cols-2">
              {inProgress.map((s) => (
                <li key={s.id}>
                  <SessionCard session={s} protocol={protocolFor(s)} onOpen={openSession} highlight />
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="mt-4 grid gap-6 xl:grid-cols-2">
          {/* Ready-to-dispense queue — the staged flow's front door */}
          <section aria-label="Ready to dispense">
            <h2 className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Ready to dispense {staged.length > 0 && <span className="tabular-nums">({staged.length.toLocaleString()})</span>}
            </h2>
            {staged.length === 0 ? (
              <EmptyState
                icon={Inbox}
                title="Nothing staged"
                hint="Stage an assignment when it arrives — at the cabinet it's scan, tap, done."
              />
            ) : (
              <ul className="space-y-2">
                {staged.map((s) => (
                  <li key={s.id}>
                    <SessionCard session={s} protocol={protocolFor(s)} onOpen={openSession} highlight />
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Recent sessions (WP-3.3: protocol labels + the History hand-off) */}
          <section aria-label="Recent sessions">
            <div className="mb-2 flex items-center justify-between gap-2">
              <h2 className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Recent sessions</h2>
              <Link
                to="/ixverify/history"
                className="inline-flex min-h-[44px] items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-800 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 dark:hover:text-primary-300 sm:min-h-0"
              >
                View all history
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
            {recent.length === 0 ? (
              <EmptyState icon={CalendarClock} title="No sessions yet" hint="Start your first dispense verification." />
            ) : (
              <ul className="space-y-2">
                {recent.map((s) => (
                  <li key={s.id}>
                    <SessionCard session={s} protocol={protocolFor(s)} onOpen={openSession} />
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
        </>
      )}

      {/* Report preview — a sealed session opens straight to its PDF */}
      {reportTarget && (
        <ReportPreview
          sessionId={reportTarget.id}
          verificationCode={reportTarget.verification_code}
          onClose={() => setReportTarget(null)}
        />
      )}

      {/* Coordinator staging modal (§3.5 — desktop or phone, any time) */}
      {stageOpen && (
        <Modal isOpen title="Stage an assignment" onClose={() => setStageOpen(false)} maxWidth="max-w-md">
          <div className="space-y-3 p-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Upload the IXRS assignment (PDF or photo) or paste its text. It waits in the Ready-to-dispense queue with
              everything pre-read — the at-cabinet flow is then scan, tap, done.
            </p>
            <div>
              <button type="button" className={secondaryBtnCls} onClick={() => fileInputRef.current?.click()}>
                <UploadCloud className="h-4 w-4" aria-hidden="true" /> {stageFile ? stageFile.name : 'Choose a file'}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,application/pdf"
                className="hidden"
                onChange={(e) => {
                  setStageFile(e.target.files?.[0] || null);
                  e.target.value = '';
                }}
                data-testid="stage-file-input"
              />
            </div>
            <div className="text-center text-xs uppercase tracking-widest text-gray-400">or</div>
            <textarea
              aria-label="Assignment text"
              className={`${fieldCls} w-full font-mono`}
              rows={5}
              placeholder="Paste the assignment email or text"
              value={stageText}
              onChange={(e) => setStageText(e.target.value)}
              disabled={!!stageFile}
            />
            <div className="flex justify-end gap-2">
              <button type="button" className={secondaryBtnCls} onClick={() => setStageOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className={primaryBtnCls}
                disabled={staging || (!stageFile && !stageText.trim())}
                onClick={() => void submitStage()}
                data-testid="stage-submit"
              >
                {staging && <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />} Stage it
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
